import axios from 'axios';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

import { ENV_CONFIG } from '../../shared/config/env.config';
import { toast } from 'react-toastify';

type DynamicBaseQueryArgs = FetchArgs & {
  service?: string;
};

const SERVICE_URL_MAP: Record<string, string> = {
  php1: ENV_CONFIG.php1Url,
  php3: ENV_CONFIG.php3Url,
  phpTntp: ENV_CONFIG.phpTntpUrl,
  djangoTnEmis: ENV_CONFIG.djangoTnEmisUrl,
  djangoEmis: ENV_CONFIG.djangoEmisUrl,
  emisLogin: ENV_CONFIG.emisLoginUrl,
  djangoTntp: ENV_CONFIG.djangoTntpUrl,
  djangoSupport: ENV_CONFIG.djangoSupportUrl,
  loginApiUrl: ENV_CONFIG.loginApiUrl,
  readingFileApi: ENV_CONFIG.readingFileApi,
};

const getApiErrorMessage = (status: number | string, data: any) => {
  const backendMessage =
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.msg ||
    (typeof data === 'string' ? data : '');

  switch (status) {
    case 'FETCH_ERROR':
      return (
        backendMessage ||
        'Unable to connect to server. Please check your network or server status.'
      );

    case 0:
      return 'Unable to connect to server. Please check your internet connection or server status.';

    case 400:
      return (
        backendMessage || 'Invalid request. Please check the submitted data.'
      );

    case 401:
      return backendMessage || 'Your session has expired. Please login again.';

    case 403:
      return (
        backendMessage || 'You do not have permission to perform this action.'
      );

    case 404:
      return backendMessage || 'Requested data was not found.';

    case 408:
      return backendMessage || 'Request timeout. Please try again.';

    case 422:
      return (
        backendMessage || 'Validation failed. Please check the entered details.'
      );

    case 429:
      return (
        backendMessage || 'Too many requests. Please try again after some time.'
      );

    case 500:
      return 'Internal server error occurred.';

    case 502:
      return (
        backendMessage || 'Bad gateway. Server is not responding properly.'
      );

    case 503:
      return (
        backendMessage ||
        'Service temporarily unavailable. Please try again later.'
      );

    case 504:
      return (
        backendMessage || 'Gateway timeout. Server took too long to respond.'
      );

    default:
      return backendMessage || 'Something went wrong. Please try again later.';
  }
};

export const dynamicBaseQuery: BaseQueryFn<
  string | DynamicBaseQueryArgs,
  unknown,
  FetchBaseQueryError
> = async (args, _api, _extraOptions) => {
  let service: string = 'php1';
  let modifiedArgs: FetchArgs;

  if (typeof args === 'string') {
    modifiedArgs = { url: args };
  } else {
    const { service: extractedService, ...restArgs } = args;
    service = extractedService ?? 'php1';
    modifiedArgs = restArgs;
  }
  console.log('Service:', service);
  console.log('SERVICE_URL_MAP:', SERVICE_URL_MAP);
  const baseUrl = SERVICE_URL_MAP[service];
  console.log('service', service);
  const {
    url,
    method = 'GET',
    body,
    params,
    headers: argHeaders,
  } = modifiedArgs;

  const authKey = localStorage.getItem('AuthorizationKey');
  const token = localStorage.getItem('token');

  console.log('Authorization:', authKey);
  console.log('token:', localStorage.getItem('token'));

  try {
    console.log('authkey', authKey);
    const result = await axios({
      url: service == 'readingFileApi' ? baseUrl : `${baseUrl}/${url}`,
      method,
      data: body,
      params,
      headers:
        service == 'readingFileApi'
          ? {}
          : {
              Accept: 'application/json, text/plain, */*',
              ...(url.includes('login') ? { 'x-auth-type': 1 } : {}),
              ...(authKey && { Authorization: authKey }),
              ...(token && { Token: token }),
              ...(argHeaders as Record<string, string>),
            },
    });

    return { data: result.data };
  } catch (error: any) {
    const status = error.response?.status ?? 'FETCH_ERROR';
    const responseData = error.response?.data ?? error.message;

    const errorMessage = getApiErrorMessage(status, responseData);
    toast.error(errorMessage);

    console.error('API Error:', {
      status,
      url: error.config?.url,
      method: error.config?.method,
      data: responseData,
      message: errorMessage,
    });

    if (status === 401) {
      localStorage.clear();
      window.location.href = '/';
    }

    return {
      error: {
        status,
        data: {
          originalError: responseData,
          message: errorMessage,
        },
      } as FetchBaseQueryError,
    };
  }
};
